import { getDefaultStore } from "jotai"
import { productsDictAtom } from "../global/AtomsProvider"

/**
 * Utility functions for calculating ingredient-related prices
 */

/**
 * Calculates the purchase price for a single ingredient, handling composite ingredients recursively.
 * Uses stored historical prices when available (for viewing existing records),
 * falls back to current prices from productsDict (for new items being added).
 * @param {Object} ingredient - The ingredient object with id, quantity, and optionally purchasePrice/packagePrice
 * @returns {number} The calculated purchase price for the ingredient
 */
export const calculateIngredientPurchasePrice = (ingredient) => {
  // Use stored historical price if available (viewing existing manufacture/operation)
  if (ingredient.purchasePrice !== undefined) {
    return (ingredient.purchasePrice || 0) + (ingredient.packagePrice || 0)
  }

  // Fall back to current price from productsDict (new items being added)
  const jotaiStore = getDefaultStore()
  const productsDict = jotaiStore.get(productsDictAtom)
  const product = productsDict[ingredient.id]

  if (!product) return 0

  // If the ingredient is composite, calculate from its ingredients recursively
  if (product.isComposite && product.ingredients && product.ingredients.length > 0) {
    let compositePrice = 0
    for (const subIngredient of product.ingredients) {
      compositePrice += calculateIngredientPurchasePrice(subIngredient) * subIngredient.quantity
    }
    return compositePrice
  }

  // For regular ingredients, use purchasePrice + packagePrice
  return (product.purchasePrice || 0) + (product.packagePrice || 0)
}

/**
 * Calculates the total purchase price from an array of ingredients
 * @param {Array} ingredients - Array of ingredient objects
 * @returns {number} The total calculated purchase price
 */
export const calculateTotalPurchasePriceFromIngredients = (ingredients) => {
  let totalPrice = 0
  for (const ingredient of ingredients) {
    const ingredientPrice = calculateIngredientPurchasePrice(ingredient)
    totalPrice += ingredientPrice * ingredient.quantity
  }
  return totalPrice
}
